import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useMyVW } from '../../context/myVWContext';

interface UseMyVWQueryParamsResult {
  token: string;
  brand: string;
  country: string;
  buildSearch: (additionalParams?: Record<string, string>) => string;
}

/**
 * Resolves token/brand/country with priority: URL query params → context (sessionStorage fallback).
 * When all three params are present in the URL, syncs them to context/sessionStorage via init().
 * Provides `buildSearch()` to construct a search string for internal myVW navigations,
 * always carrying token/brand/country forward and preserving any extra URL params already present.
 */
export const useMyVWQueryParams = (): UseMyVWQueryParamsResult => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const {
    token: contextToken,
    brand: contextBrand,
    country: contextCountry,
    init,
  } = useMyVW();

  const initRef = useRef(init);
  initRef.current = init;

  const tokenParam = searchParams.get('token') ?? '';
  const brandParam = searchParams.get('brand') ?? '';
  const countryParam = searchParams.get('country') ?? '';

  // Track last synced params to prevent duplicate init() calls for identical values
  const lastSyncedRef = useRef<{ token: string; brand: string; country: string } | null>(null);

  useEffect(() => {
    if (!tokenParam || !brandParam || !countryParam) return;
    const last = lastSyncedRef.current;
    if (last && last.token === tokenParam && last.brand === brandParam && last.country === countryParam) return;
    lastSyncedRef.current = { token: tokenParam, brand: brandParam, country: countryParam };
    initRef.current({ token: tokenParam, brand: brandParam, country: countryParam });
  }, [tokenParam, brandParam, countryParam]);

  const token = tokenParam || contextToken || '';
  const brand = brandParam || contextBrand || '';
  const country = countryParam || contextCountry || '';

  const buildSearch = (additionalParams?: Record<string, string>): string => {
    const params = new URLSearchParams(location.search);

    if (token) params.set('token', token);
    if (brand) params.set('brand', brand);
    if (country) params.set('country', country);

    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
    }

    const str = params.toString();
    return str ? `?${str}` : '';
  };

  return { token, brand, country, buildSearch };
};
